// src/components/SPanel/Home/LessonsCarousel.jsx
//
// Caruselul lecțiilor de pe pagina principală: toate lecțiile pachetului, în
// ordine, cu lecția următoare în centru. Cele din lateral stau „una sub alta”,
// rotite spre centru și tot mai departe (perspectivă), ca să se vadă că se
// poate derula. Fără săgeți: se trage cu degetul / mouse-ul, cu trackpad-ul
// (orizontal) sau cu săgețile tastaturii; un clic pe o lecție din lateral o
// aduce în centru, un clic pe cea din centru o deschide.
//
// Poziția e un număr real (2.4 = între lecția a 3-a și a 4-a) și se animă cu
// requestAnimationFrame, ca tragerea și revenirea să fie la fel de fine.

import React, {
   useCallback,
   useEffect,
   useLayoutEffect,
   useRef,
   useState,
} from "react";

import { formatDayMonth, formatTime } from "./homeLessons";
import { prefersReducedMotion } from "../../../utils/appearanceSettings";

const STATE_LABELS = {
   done: "Efectuată",
   live: "Acum",
   scheduled: "Programată",
   todo: "Neprogramată",
};

// câte lecții se văd de fiecare parte a celei din centru
const SIDE_VISIBLE = 4;
// distanța până la prima vecină și între următoarele, în lățimi de lecție
const NEAR = 0.86;
const FAR = 0.4;
// o mișcare mai mică de atât e clic, nu tragere (degetul tremură mai mult
// decât mouse-ul)
const CLICK_SLOP_PX = { mouse: 6, touch: 12, pen: 10 };
// apăsarea lungă nu deschide lecția
const TAP_MAX_MS = 600;
// dacă degetul a stat pe loc atâtea ms înainte de eliberare, nu mai „aruncă”
const FLING_IDLE_MS = 90;
// cât de departe poate duce o aruncare, în lecții
const FLING_MAX = 3;

const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
const easeInOutCubic = (t) =>
   t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

/** Așezarea unei lecții după distanța ei față de centru (`offset`, în lecții). */
function cardStyle(offset, cardWidth) {
   const abs = Math.abs(offset);
   const sign = Math.sign(offset);
   // prima vecină stă mai departe, următoarele se strâng una sub alta
   const near = cardWidth * NEAR;
   const far = cardWidth * FAR;
   const x = sign * (abs <= 1 ? abs * near : near + (abs - 1) * far);
   const z = -Math.min(abs, SIDE_VISIBLE + 1) * 150;
   const rotate = -sign * Math.min(abs, 1) * 32;
   const opacity = abs > SIDE_VISIBLE + 0.5 ? 0 : 1 - Math.max(0, abs - 1) * 0.2;

   return {
      transform: `translateX(${x}px) translateZ(${z}px) rotateY(${rotate}deg)`,
      opacity,
      zIndex: 1000 - Math.round(abs * 10),
      visibility: abs > SIDE_VISIBLE + 1 ? "hidden" : "visible",
      // umbra „luminii” de pe lecțiile din lateral
      "--shade": Math.min(1, abs * 0.45).toFixed(3),
   };
}

function LessonCard({ item, compact }) {
   const label = item.isNext
      ? item.state === "live"
         ? "Acum"
         : "Următoarea"
      : STATE_LABELS[item.state];

   const when = item.start ? formatDayMonth(item.start) : null;

   // widget scund (2×1 pe telefon): starea, numărul și data, pe scurt
   if (compact) {
      return (
         <>
            <div className="shome-lesson__top">
               <span className={`shome-lesson__badge shome-lesson__badge--${item.state}`}>
                  {label}
               </span>
            </div>
            <div className="shome-lesson__number">
               <strong>{String(item.number).padStart(2, "0")}</strong>
            </div>
            <span className="shome-lesson__date">
               {when
                  ? `${when.day} ${when.month} · ${formatTime(item.start)}`
                  : "Neprogramată"}
            </span>
         </>
      );
   }

   return (
      <>
         <div className="shome-lesson__top">
            <span className={`shome-lesson__badge shome-lesson__badge--${item.state}`}>
               {item.state === "done" && (
                  <svg viewBox="0 0 24 24" aria-hidden="true">
                     <path d="M9 16.2 4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4z" />
                  </svg>
               )}
               {label}
            </span>
            {item.gearbox && (
               <span className="shome-lesson__gear">{item.gearbox}</span>
            )}
         </div>

         <div className="shome-lesson__number">
            <span>Lecția</span>
            <strong>{String(item.number).padStart(2, "0")}</strong>
         </div>

         {when ? (
            <div className="shome-lesson__when">
               <span className="shome-lesson__date">
                  {when.weekday}, {when.day} {when.month}
               </span>
               <span className="shome-lesson__time">
                  {formatTime(item.start)}
               </span>
            </div>
         ) : (
            <div className="shome-lesson__when">
               <span className="shome-lesson__date">Încă nu e programată</span>
            </div>
         )}

         {item.plate && (
            <div className="shome-lesson__meta">
               <span className="shome-lesson__plate">{item.plate}</span>
            </div>
         )}
      </>
   );
}

export default function LessonsCarousel({ items, focusIndex, onOpenLesson }) {
   const viewportRef = useRef(null);
   const posRef = useRef(0);
   const rafRef = useRef(0);
   const dragRef = useRef(null);
   const wheelTimerRef = useRef(0);
   const touchedRef = useRef(false);
   const introDoneRef = useRef(false);

   const [pos, setPosState] = useState(0);
   const [cardWidth, setCardWidth] = useState(240);
   const [stageHeight, setStageHeight] = useState(240);
   const [dragging, setDragging] = useState(false);

   const count = items.length;
   const maxIndex = Math.max(0, count - 1);

   const setPos = useCallback((value) => {
      posRef.current = value;
      setPosState(value);
   }, []);

   const stopAnimation = useCallback(() => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
   }, []);

   const animateTo = useCallback(
      (target, { duration = 480, easing = easeOutCubic } = {}) => {
         stopAnimation();
         const to = clamp(target, 0, maxIndex);
         const from = posRef.current;
         if (prefersReducedMotion() || Math.abs(to - from) < 0.001) {
            setPos(to);
            return;
         }
         const startedAt = performance.now();
         const step = (now) => {
            const t = Math.min(1, (now - startedAt) / duration);
            setPos(from + (to - from) * easing(t));
            rafRef.current = t < 1 ? requestAnimationFrame(step) : 0;
         };
         rafRef.current = requestAnimationFrame(step);
      },
      [maxIndex, setPos, stopAnimation],
   );

   useEffect(() => () => {
      stopAnimation();
      window.clearTimeout(wheelTimerRef.current);
   }, [stopAnimation]);

   // lecțiile se potrivesc pe lățimea și înălțimea widget-ului
   useLayoutEffect(() => {
      const node = viewportRef.current;
      if (!node || typeof ResizeObserver === "undefined") return undefined;
      const observer = new ResizeObserver(([entry]) => {
         const { width, height } = entry.contentRect;
         setCardWidth(clamp(Math.round(width * 0.24), 140, 290));
         setStageHeight(Math.round(height));
      });
      observer.observe(node);
      return () => observer.disconnect();
   }, []);

   // la prima încărcare caruselul „zboară” de la prima lecție la cea următoare
   useEffect(() => {
      if (!count) return;
      if (introDoneRef.current) {
         if (!touchedRef.current) animateTo(focusIndex);
         return;
      }
      introDoneRef.current = true;
      if (prefersReducedMotion()) {
         setPos(clamp(focusIndex, 0, maxIndex));
         return;
      }
      const distance = Math.abs(focusIndex);
      if (distance >= 1) {
         animateTo(focusIndex, {
            duration: Math.min(1700, 500 + distance * 90),
            easing: easeInOutCubic,
         });
      } else {
         // lecția următoare e prima: o mică „împingere” arată că se derulează
         setPos(0);
         const timers = [
            window.setTimeout(() => !touchedRef.current && animateTo(0.45, { duration: 420 }), 500),
            window.setTimeout(() => !touchedRef.current && animateTo(0, { duration: 520 }), 930),
         ];
         return () => timers.forEach((timer) => window.clearTimeout(timer));
      }
      return undefined;
   }, [count, focusIndex, maxIndex, animateTo, setPos]);

   /* ——— tragere ——— */
   // Clicurile greșite se evită așa: o atingere care oprește caruselul în
   // mișcare doar îl oprește; începutul unei derulări a paginii (sus / jos)
   // nu e clic; o lecție se deschide doar când caruselul stă pe ea.
   const onPointerDown = (event) => {
      if (event.button !== undefined && event.button !== 0) return;
      touchedRef.current = true;
      const wasMoving =
         Boolean(rafRef.current) ||
         Math.abs(posRef.current - Math.round(posRef.current)) > 0.05;
      stopAnimation();
      const card = event.target.closest?.("[data-index]");
      dragRef.current = {
         id: event.pointerId,
         slop: CLICK_SLOP_PX[event.pointerType] || CLICK_SLOP_PX.mouse,
         startX: event.clientX,
         startY: event.clientY,
         startT: performance.now(),
         startPos: posRef.current,
         lastX: event.clientX,
         lastT: performance.now(),
         velocity: 0,
         moved: false,
         vertical: false,
         wasMoving,
         index: card ? Number(card.dataset.index) : null,
      };
   };

   const onPointerMove = (event) => {
      const drag = dragRef.current;
      if (!drag || drag.id !== event.pointerId) return;
      const dx = event.clientX - drag.startX;
      const dy = event.clientY - drag.startY;
      if (!drag.moved) {
         // degetul pleacă mai mult în sus / jos: e derularea paginii
         if (Math.abs(dy) > drag.slop && Math.abs(dy) > Math.abs(dx)) {
            drag.vertical = true;
         }
         if (drag.vertical || Math.abs(dx) < drag.slop) return;
         drag.moved = true;
         setDragging(true);
         viewportRef.current?.setPointerCapture?.(event.pointerId);
      }
      const step = cardWidth * NEAR;
      let next = drag.startPos - dx / step;
      // peste capete se trage tot mai greu
      if (next < 0) next = -Math.sqrt(-next) * 0.5;
      if (next > maxIndex) next = maxIndex + Math.sqrt(next - maxIndex) * 0.5;
      setPos(next);

      // viteza (px/ms), netezită ca o mișcare scurtă să nu dea un salt
      const now = performance.now();
      const dt = Math.max(8, now - drag.lastT);
      const velocity = (event.clientX - drag.lastX) / dt;
      drag.velocity = drag.velocity * 0.3 + velocity * 0.7;
      drag.lastX = event.clientX;
      drag.lastT = now;
   };

   const finishDrag = (event, cancelled) => {
      const drag = dragRef.current;
      if (!drag || drag.id !== event.pointerId) return;
      dragRef.current = null;
      setDragging(false);
      const settle = () => animateTo(Math.round(clamp(posRef.current, 0, maxIndex)));

      if (!drag.moved) {
         const isTap =
            !cancelled &&
            !drag.vertical &&
            !drag.wasMoving &&
            drag.index != null &&
            performance.now() - drag.startT <= TAP_MAX_MS;
         if (!isTap) {
            settle();
            return;
         }
         const current = Math.round(posRef.current);
         if (drag.index === current) onOpenLesson(items[current]);
         else animateTo(drag.index);
         return;
      }
      if (cancelled) {
         settle();
         return;
      }
      // aruncarea continuă puțin în direcția tragerii
      const step = cardWidth * NEAR;
      const idle = performance.now() - drag.lastT > FLING_IDLE_MS;
      const fling = idle
         ? 0
         : clamp((-drag.velocity * 160) / step, -FLING_MAX, FLING_MAX);
      animateTo(Math.round(clamp(posRef.current + fling, 0, maxIndex)));
   };

   /* ——— trackpad (derulare orizontală) ——— */
   useEffect(() => {
      const node = viewportRef.current;
      if (!node) return undefined;
      const onWheel = (event) => {
         if (Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
         event.preventDefault();
         touchedRef.current = true;
         stopAnimation();
         const step = cardWidth * NEAR;
         setPos(clamp(posRef.current + event.deltaX / step, -0.3, maxIndex + 0.3));
         window.clearTimeout(wheelTimerRef.current);
         wheelTimerRef.current = window.setTimeout(
            () => animateTo(Math.round(clamp(posRef.current, 0, maxIndex))),
            140,
         );
      };
      node.addEventListener("wheel", onWheel, { passive: false });
      return () => node.removeEventListener("wheel", onWheel);
   }, [animateTo, cardWidth, maxIndex, setPos, stopAnimation]);

   const onKeyDown = (event) => {
      const current = Math.round(posRef.current);
      if (event.key === "ArrowRight") {
         event.preventDefault();
         touchedRef.current = true;
         animateTo(current + 1);
      } else if (event.key === "ArrowLeft") {
         event.preventDefault();
         touchedRef.current = true;
         animateTo(current - 1);
      } else if (event.key === "Enter") {
         event.preventDefault();
         onOpenLesson(items[current]);
      }
   };

   if (!count) return null;

   const cardHeight = clamp(stageHeight - 16, 80, 260);
   const compact = cardHeight < 170;
   // puțin mai scund decât e nevoie pentru tot: fără numărul mașinii
   const tight = !compact && cardHeight < 240;
   const current = clamp(Math.round(pos), 0, maxIndex);
   // centrul liniuței lecției curente, pe șină
   const thumbLeft = ((clamp(pos, 0, maxIndex) + 0.5) / count) * 100;

   return (
      <div className="shome-carousel">
         <div
            ref={viewportRef}
            className={`shome-carousel__viewport${dragging ? " is-dragging" : ""}`}
            tabIndex={0}
            role="listbox"
            aria-label="Lecțiile tale, în ordine"
            aria-activedescendant={`shome-lesson-${current}`}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={(event) => finishDrag(event, false)}
            onPointerCancel={(event) => finishDrag(event, true)}
            onKeyDown={onKeyDown}
         >
            <div className="shome-carousel__stage">
               {items.map((item, index) => {
                  const offset = index - pos;
                  if (Math.abs(offset) > SIDE_VISIBLE + 1.5) return null;
                  return (
                     <div
                        key={item.key}
                        id={`shome-lesson-${index}`}
                        role="option"
                        aria-selected={index === current}
                        data-index={index}
                        className={
                           "shome-lesson" +
                           ` shome-lesson--${item.state}` +
                           (item.isNext ? " shome-lesson--next" : "") +
                           (compact ? " shome-lesson--compact" : "") +
                           (tight ? " shome-lesson--tight" : "") +
                           (index === current ? " is-active" : "")
                        }
                        style={{
                           width: cardWidth,
                           height: cardHeight,
                           marginLeft: -cardWidth / 2,
                           top: Math.max(0, (stageHeight - cardHeight) / 2),
                           ...cardStyle(offset, cardWidth),
                        }}
                     >
                        <LessonCard item={item} compact={compact} />
                     </div>
                  );
               })}
            </div>
         </div>

         {/* șina: câte o liniuță pe lecție, cu indicatorul poziției */}
         <div className="shome-carousel__rail" aria-hidden="true">
            <div className="shome-carousel__ticks">
               {items.map((item, index) => (
                  <button
                     key={item.key}
                     type="button"
                     tabIndex={-1}
                     className={
                        "shome-carousel__tick" +
                        ` shome-carousel__tick--${item.state}` +
                        (item.isNext ? " shome-carousel__tick--next" : "")
                     }
                     onClick={() => {
                        touchedRef.current = true;
                        animateTo(index);
                     }}
                  />
               ))}
               <span
                  className="shome-carousel__thumb"
                  style={{ left: `${thumbLeft}%` }}
               />
            </div>
            <span className="shome-carousel__count">
               {current + 1} / {count}
            </span>
         </div>
      </div>
   );
}
